import {RootState} from '../../../rootStore/store';
import {BaseComponentDTO} from '../types/workflow';
import {
  autoLayoutNodes,
  workflowToEdges,
  workflowToNodes,
} from '../utils/reactFlowUtils';

// Basic selectors
export const selectWorkflowState = (state: RootState) => state.workflow;

export const selectCurrentWorkflow = (state: RootState) =>
  state.workflow?.currentWorkflow;

export const selectWorkflowId = (state: RootState) =>
  state.workflow?.currentWorkflow?.id;

export const selectWorkflowLoading = (state: RootState) =>
  state.workflow?.loading ?? false;

export const selectWorkflowError = (state: RootState) => state.workflow?.error;

export const selectSelectedNodeId = (state: RootState) =>
  state.workflow?.selectedNodeId;

// Component selectors
export const selectInputComponents = (state: RootState) =>
  state.workflow?.inputComponents ?? {};

export const selectRulesetComponents = (state: RootState) =>
  state.workflow?.rulesetComponents ?? {};

export const selectInputComponentById = (id: string) => (state: RootState) =>
  state.workflow?.inputComponents[id];

export const selectRulesetComponentById = (id: string) => (state: RootState) =>
  state.workflow?.rulesetComponents[id];

// Loading state selectors
export const selectComponentLoading =
  (componentId: string) => (state: RootState) =>
    state.workflow?.componentLoading[componentId] ?? false;

export const selectIsAnyComponentLoading = (state: RootState) =>
  Object.values(state.workflow?.componentLoading ?? {}).some(Boolean);

// Computed selectors
export const selectWorkflowComponents = (state: RootState) => {
  const workflow = selectCurrentWorkflow(state);
  return workflow?.components ?? [];
};

export const selectInputComponent = (state: RootState) => {
  const components = selectWorkflowComponents(state);
  return (
    components.find(
      (component: BaseComponentDTO) => component.type === 'input'
    ) || null
  );
};

export const selectRulesetComponentList = (state: RootState) => {
  const components = selectWorkflowComponents(state);
  return components.filter(
    (component: BaseComponentDTO) => component.type === 'ruleset'
  );
};

export const selectDecisionComponents = (state: RootState) => {
  const components = selectWorkflowComponents(state);
  return components.filter(
    (component: BaseComponentDTO) => component.type === 'decision'
  );
};

// Validation selectors
export const selectIsWorkflowValid = (state: RootState) => {
  const workflow = selectCurrentWorkflow(state);
  if (!workflow) return false;

  const hasInput = workflow.components.some(
    (c: BaseComponentDTO) => c.type === 'input'
  );
  const hasAtLeastOneRule = workflow.components.some(
    (c: BaseComponentDTO) => c.type === 'ruleset'
  );
  const hasDecision = workflow.components.some(
    (c: BaseComponentDTO) => c.type === 'decision'
  );

  return hasInput && hasAtLeastOneRule && hasDecision;
};

// Graph navigation selectors
export const selectComponentById =
  (componentId: string) => (state: RootState) => {
    const workflow = selectCurrentWorkflow(state);
    return (
      workflow?.components.find(
        (c: BaseComponentDTO) => c.id === componentId
      ) || null
    );
  };

export const selectStartComponent = (state: RootState) => {
  const workflow = selectCurrentWorkflow(state);
  if (!workflow) return null;

  return (
    workflow.components.find(
      (c: BaseComponentDTO) => c.id === workflow.startComponentId
    ) || null
  );
};

// React Flow mapping selectors
export const selectReactFlowData = (state: RootState) => {
  const workflow = selectCurrentWorkflow(state);
  if (!workflow) return {nodes: [], edges: []};
  // Build nodes and edges using existing utilities which contain consistent
  // node data and handle positions
  const nodes = workflowToNodes(workflow);
  const edges = workflowToEdges(workflow);

  // Apply auto-layout — uses dagre when edges are available and falls back to
  // a simple level-based layout otherwise
  const layoutedNodes = autoLayoutNodes(nodes, edges);

  return {nodes: layoutedNodes, edges};
};
